import { UseMutationResult } from '@tanstack/react-query';
import React from 'react';
import { LuLoader } from 'react-icons/lu';

import { GUID, InternalUserProfile, TenantTeamMember } from '@ndvr/auth-api';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { MultiCombobox } from '@/components/ui/multi-combobox';

export interface ShareDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    title: string;
    description: string;
    // Who may receive the entity is the sharing endpoint's rule, so the reason an empty list
    // is empty differs per share flow.
    emptyLabel: string;
    recipients: { data: TenantTeamMember[]; isPending: boolean; isError: boolean };
    mutation: UseMutationResult<void, Error, GUID<InternalUserProfile>[]>;
}

// Presentational share dialog, sibling to ConfirmModal: copy, eligible recipients, and the
// mutation all arrive as props, so every shareable entity adds only a thin wrapper holding
// its own eligibility query and mutation hook. The dialog never learns what is being shared —
// each share mutation binds its target in the hook and takes only the recipients in `mutate`.
export const ShareDialog = ({
    open,
    onOpenChange,
    title,
    description,
    emptyLabel,
    recipients,
    mutation,
}: ShareDialogProps) => {
    const [recipientGUIDs, setRecipientGUIDs] = React.useState<GUID<InternalUserProfile>[]>([]);

    const availableRecipients = recipients.data;
    const availableRecipientGUIDs = availableRecipients.map((member) => member.internalUserGUID);
    const allRecipientsSelected =
        availableRecipientGUIDs.length > 0 &&
        availableRecipientGUIDs.every((recipientGUID) => recipientGUIDs.includes(recipientGUID));
    const someRecipientsSelected = availableRecipientGUIDs.some((recipientGUID) =>
        recipientGUIDs.includes(recipientGUID),
    );

    const handleOpenChange = (nextOpen: boolean) => {
        if (!nextOpen && !mutation.isPending) {
            setRecipientGUIDs([]);
        }
        onOpenChange(nextOpen);
    };

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        mutation.mutate(recipientGUIDs, {
            onSuccess: () => {
                setRecipientGUIDs([]);
                onOpenChange(false);
            },
        });
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="sm:max-w-xl">
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                    <DialogDescription>{description}</DialogDescription>
                </DialogHeader>
                <form className="space-y-5" onSubmit={handleSubmit}>
                    <div className="space-y-3">
                        {recipients.isPending ? (
                            <div className="text-muted-foreground flex items-center gap-2 text-sm">
                                <LuLoader className="animate-spin" /> Loading team members…
                            </div>
                        ) : recipients.isError ? (
                            <p className="text-destructive text-sm" role="alert">
                                Team members could not be loaded. Close the dialog and try again.
                            </p>
                        ) : availableRecipients.length === 0 ? (
                            <p className="text-muted-foreground text-sm">{emptyLabel}</p>
                        ) : (
                            <div className="space-y-3">
                                <div className="flex items-center gap-2">
                                    <Checkbox
                                        id="share-select-all-recipients"
                                        checked={
                                            allRecipientsSelected || (someRecipientsSelected ? 'indeterminate' : false)
                                        }
                                        onCheckedChange={() =>
                                            setRecipientGUIDs(allRecipientsSelected ? [] : availableRecipientGUIDs)
                                        }
                                    />
                                    <label
                                        className="cursor-pointer text-sm font-medium"
                                        htmlFor="share-select-all-recipients"
                                    >
                                        Select all team members
                                    </label>
                                </div>
                                {availableRecipients.length > 3 ? (
                                    <MultiCombobox
                                        options={availableRecipients.map((member) => ({
                                            value: member.internalUserGUID,
                                            label: member.name || member.email,
                                        }))}
                                        value={recipientGUIDs}
                                        onValueChange={setRecipientGUIDs}
                                        placeholder="Select team members"
                                        noOptionsLabel="No team member found"
                                    />
                                ) : (
                                    <div className="space-y-2">
                                        {availableRecipients.map((member) => {
                                            const checkboxId = `share-recipient-${member.internalUserGUID}`;
                                            const selected = recipientGUIDs.includes(member.internalUserGUID);
                                            return (
                                                <div key={member.internalUserGUID} className="flex items-center gap-2">
                                                    <Checkbox
                                                        id={checkboxId}
                                                        checked={selected}
                                                        onCheckedChange={() =>
                                                            setRecipientGUIDs((current) =>
                                                                selected
                                                                    ? current.filter(
                                                                          (recipientGUID) =>
                                                                              recipientGUID !== member.internalUserGUID,
                                                                      )
                                                                    : [...current, member.internalUserGUID],
                                                            )
                                                        }
                                                    />
                                                    <label className="cursor-pointer text-sm" htmlFor={checkboxId}>
                                                        {member.name || member.email}
                                                        {member.name && member.email && (
                                                            <span className="text-muted-foreground">
                                                                {' '}
                                                                — {member.email}
                                                            </span>
                                                        )}
                                                    </label>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            disabled={mutation.isPending}
                            onClick={() => handleOpenChange(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={
                                mutation.isPending ||
                                recipients.isPending ||
                                recipients.isError ||
                                recipientGUIDs.length === 0
                            }
                        >
                            {mutation.isPending && <LuLoader className="animate-spin" />}
                            Share
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};
